import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  UploadCloud, Info, Activity, Package,
  Settings, Banknote, FileText, Trash2,
  ShieldCheck, Tag, Hash, Layers, Shield, Factory, Clock
} from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import { PartDto, PartDetailsDto } from "@/types";
import { Select, message } from "antd";
import InstrumentLoader from "@/components/InstrumentLoader";
import { PART_STATES, PART_CATEGORIES } from "../enums";
import ConfirmationAlert from "@/components/ConfirmationAlert";

const GlassCard = ({ children, className = "", title, icon: Icon }: any) => (
  <div className={`bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl flex flex-col h-full ${className}`}>
    {title && (
      <div className="flex items-center gap-2 mb-5 border-b border-slate-800/50 pb-4">
        {Icon && <Icon size={18} className="text-blue-400" />}
        <h3 className="text-xs font-black uppercase tracking-[0.15em] text-white">{title}</h3>
      </div>
    )}
    <div className="flex-1">{children}</div>
  </div>
);

const InputField = "w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-[#334155] text-white placeholder:text-slate-500 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none";
const Label = "text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block ml-1";
const commonSelectProps = {
  variant: "borderless" as const,
  className: "w-full h-11 bg-[#1E293B] border border-[#334155] rounded-xl text-white",
  dropdownStyle: { backgroundColor: '#0F172A', border: '1px solid #334155', color: 'white' },
  styles: {
    selection: { color: 'white', paddingLeft: '8px' },
    input: { color: 'white' }
  }
};

const AircraftPart = () => {
  const { saveAircraftPart, updateAircraftPart } = useAppContext();
  const { currentUser, partCommissionRate, syncSystemCommissionRates, getDictValue } = useAuth() as any;
  const { params, navigateTo } = useNavigation();
  const [loading, setLoading] = useState(false);

  const isInitialMount = useRef(true);
  const partFromParams = params?.aircraftPart as (PartDto & Partial<PartDetailsDto>) | undefined;
  const isEditMode = !!partFromParams;

  const [existingMedia, setExistingMedia] = useState<{id: number, url: string}[]>([]);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [price, setPrice] = useState<number | "">("");
  const [commissionModel, setCommissionModel] = useState("DEDUCT_FROM_PRICE");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isDraftAlertOpen, setIsDraftAlertOpen] = useState(false);

  const [formData, setFormData] = useState<Partial<PartDto & PartDetailsDto>>({
    category: undefined,
    state: undefined,
    title: "",
    manufacturer: "",
    description: "",
    serialNumber: "",
    quantity: 1,
    commissionModel: "DEDUCT_FROM_PRICE"
  });

  const alertDict = {
    title: "Verificare Necesară",
    subtitle: isEditMode ? "Re-evaluare Componentă" : "Validare Piesă Nouă",
    description: isEditMode
      ? "Ai ales să modifici o piesă din inventar. Pentru a menține standardele de siguranță aviatică, orice modificare trimite piesa înapoi în lista de așteptare pentru verificare."
      : "Piesa este aproape gata pentru marketplace! Înainte de a fi publicată oficial, aceasta va trece printr-un proces rapid de verificare a documentelor și stării tehnice.",
    info: isEditMode
      ? [
          "Piesa va fi ascunsă temporar din marketplace în timpul re-verificării.",
          "Echipa tehnică va revizui modificările aduse descrierii și stării.",
          "După aprobare, piesa va fi republicată automat cu noile date."
        ]
      : [
          "Echipa noastră va valida detaliile tehnice și compatibilitățile introduse.",
          "Se va verifica mențiunea certificărilor (EASA Form 1 / FAA Form 8130-3).",
          "Odată aprobată, piesa devine vizibilă instant în stocul global."
        ],
    btnCancel: "Înapoi la formular",
    btnConfirm: "Am înțeles, publică"
  };

  const draftAlertDict = {
    title: "SALVARE ÎN MOD DRAFT",
    subtitle: "Listarea nu va fi publică",
    description: "Anunțul va fi salvat în contul tău fără a fi trimis spre verificare.",
    info: [
      "Această piesă NU va fi vizibilă public în marketplace.",
      "Oricând poți reveni pentru a completa datele lipsă și a o trimite spre aprobare.",
      "Anunțul va rămâne salvat în siguranță în panoul tău de administrare."
    ],
    btnCancel: "Înapoi la editare",
    btnConfirm: "Confirmă Salvare Draft"
  };

  const COMMISSION_MODELS = [
    { code: "DEDUCT_FROM_PRICE", value: "Deduce din prețul total (Comision inclus)" },
    { code: "ADD_ON_TOP", value: "Adaugă peste preț (Comision adăugat peste)" }
  ];

  useEffect(() => {
    if (syncSystemCommissionRates) {
      syncSystemCommissionRates().catch((err: any) => {
        console.error("Nu s-au putut sincroniza comisioanele globale la montarea paginii:", err);
      });
    }
  }, [syncSystemCommissionRates]);

  const isNotEmpty = (val: any) => val !== null && val !== undefined && val.toString().trim() !== "";
  const hasMedia = existingMedia.length > 0 || mediaFiles.length > 0;

  const computedValues = useMemo(() => {
    const inputPrice = Number(price) || 0;
    const rate = Number(partCommissionRate) || 0;

    if (commissionModel === "DEDUCT_FROM_PRICE") {
      const grossValue = inputPrice;
      const commissionValue = grossValue * rate;
      const netValue = grossValue - commissionValue;
      return { grossValue, commissionValue, netValue, rate };
    } else {
      const netValue = inputPrice;
      const commissionValue = netValue * rate;
      const grossValue = netValue + commissionValue;
      return { grossValue, commissionValue, netValue, rate };
    }
  }, [price, commissionModel, partCommissionRate]);

  useEffect(() => {
    if (partFromParams && isInitialMount.current) {
      setFormData(partFromParams);

      if (partFromParams.mediaUrls && partFromParams.mediaUrls.length > 0) {
        const mappedUrls = partFromParams.mediaUrls.map((urlStr, index) => ({
          id: index,
          url: urlStr
        }));
        setExistingMedia(mappedUrls);
      } else if (partFromParams.media) {
        const mapped = (partFromParams.media as any).map((m: any, index: number) => ({
          id: index,
          url: m.mediaData.startsWith('data:') ? m.mediaData : `data:image/jpeg;base64,${m.mediaData}`
        }));
        setExistingMedia(mapped);
      }

      setCommissionModel(partFromParams.commissionModel || "DEDUCT_FROM_PRICE");
      if (partFromParams.commissionModel === "ADD_ON_TOP") {
        setPrice(partFromParams.netValue || "");
      } else {
        setPrice(partFromParams.grossValue || "");
      }

      isInitialMount.current = false;
    }
  }, [partFromParams]);

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setMediaFiles([...mediaFiles, ...Array.from(e.target.files)]);
  };

  const removeNewFile = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const executeSubmit = async (isDraftMode = false) => {
    setIsAlertOpen(false);
    setIsDraftAlertOpen(false);
    setLoading(true);

    try {
      const partDto: PartDto = {
        partId: isEditMode ? partFromParams?.partId : undefined,
        sellerId: isEditMode ? partFromParams?.sellerId : undefined,
        category: formData.category,
        title: formData.title,
        manufacturer: formData.manufacturer,
        state: formData.state,
        serialNumber: formData.serialNumber,
        quantity: Number(formData.quantity),
        grossValue: computedValues.grossValue,
        media: mediaFiles,
        mediaUrls: existingMedia.map((m) => m.url),
        draft: isDraftMode,
        active: !isDraftMode,
        verified: false,
        partDetailsId: isEditMode ? partFromParams?.partDetailsId : undefined
      };

      const partDetailsDto: PartDetailsDto = {
        description: formData.description,
        commissionModel: commissionModel,
        commissionRate: computedValues.rate,
        commissionValue: computedValues.commissionValue,
        netValue: computedValues.netValue,
        chargingModel: "BY_COMMISSION"
      };

      let success = false;
      if (isEditMode) {
        success = await updateAircraftPart(partDto, partDetailsDto);
      } else {
        success = await saveAircraftPart(partDto, partDetailsDto);
      }

      if (success) {
        message.success(isEditMode ? "Piesă actualizată." : "Piesă adăugată în inventar.");
        navigateTo("dashboard");
      } else {
        message.error("Eroare la procesarea piesei.");
      }
    } catch (err) {
      console.error(err);
      message.error("A apărut o eroare neașteptată.");
    } finally {
      setLoading(false);
    }
  };

  const validateAndScroll = () => {
    setIsSubmitted(true);
    setTouched({
      title: true,
      category: true,
      state: true,
      price: true,
      description: true
    });

    if (!isNotEmpty(formData.title) || !isNotEmpty(formData.category) || !isNotEmpty(formData.state)) {
      document.getElementById("identification-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!isNotEmpty(price) || Number(price) <= 0) {
      document.getElementById("finance-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!hasMedia) {
      document.getElementById("media-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!isNotEmpty(formData.description)) {
      document.getElementById("technical-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    return true;
  };

  const handleDraftClick = () => {
    setIsDraftAlertOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = validateAndScroll();

    if (isValid) {
      setIsAlertOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={loading} />

      <div className="container max-w-7xl py-12 px-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-emerald-500 pl-6">
          <div>
            <p className="text-emerald-500 font-mono text-sm tracking-[0.2em] mb-1 uppercase font-black">
              OPERAȚIUNI VÂNZĂRI // {isEditMode ? "PART_UPDATE" : "PIESĂ NOUĂ"}
            </p>
            <h1 className="text-5xl font-black text-white italic tracking-tight">
              {isEditMode ? "Editare" : "Adăugare"} <span className="text-slate-500">Piesă</span>
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
            <div className="text-right px-4">
              <p className="text-[10px] text-slate-500 uppercase font-bold">Status</p>
              <p className="text-blue-500 font-mono text-xs font-bold flex items-center gap-1">
                <Activity size={12} className="animate-pulse" /> SYSTEM_READY
              </p>
            </div>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="space-y-8">
            <div id="identification-section">
              <GlassCard title="Detalii Identificare" icon={Package}>
                <div className="space-y-4">
                  <div>
                    <label className={Label}>Titlu Anunț <span className="text-red-500">*</span></label>
                    <input
                      className={`${InputField} ${(touched.title || isSubmitted) && !isNotEmpty(formData.title) ? "border-red-500 ring-1 ring-red-500 shadow-[0_0_10px_rgba(239,68,68,0.1)]" : ""}`}
                      placeholder="ex: Motor Lycoming IO-360-A1A"
                      value={formData.title || ""}
                      onBlur={() => setTouched(prev => ({...prev, title: true}))}
                      onChange={e => setFormData({...formData, title: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className={Label}>Producător</label>
                    <input
                      className={InputField}
                      placeholder="ex: Lycoming, Garmin, Bose..."
                      value={formData.manufacturer || ""}
                      onChange={e => setFormData({...formData, manufacturer: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={Label}>Categorie <span className="text-red-500">*</span></label>
                      <Select
                        {...commonSelectProps}
                        placeholder="Selectează..."
                        className={`${commonSelectProps.className} ${(touched.category || isSubmitted) && !isNotEmpty(formData.category) ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        value={formData.category || undefined}
                        onChange={val => setFormData({...formData, category: val})}
                        onBlur={() => setTouched(prev => ({...prev, category: true}))}
                        options={PART_CATEGORIES.map(cat => ({
                          label: cat.label,
                          value: cat.code,
                          style: { color: 'white' }
                        }))}
                      />
                    </div>
                    <div>
                      <label className={Label}>Serial Number (S/N)</label>
                      <input
                        className={InputField}
                        placeholder="Introduceți S/N"
                        value={formData.serialNumber || ""}
                        onChange={e => setFormData({...formData, serialNumber: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={Label}>Stare Tehnică <span className="text-red-500">*</span></label>
                    <Select
                      {...commonSelectProps}
                      placeholder="Selectează starea piesei..."
                      className={`${commonSelectProps.className} ${(touched.state || isSubmitted) && !isNotEmpty(formData.state) ? "border-red-500 ring-1 ring-red-500" : ""}`}
                      value={formData.state || undefined}
                      onChange={val => setFormData({...formData, state: val})}
                      onBlur={() => setTouched(prev => ({...prev, state: true}))}
                      options={PART_STATES.map(s => ({
                        label: s.label,
                        value: s.code,
                        style: { color: 'white' }
                      }))}
                    />
                  </div>
                </div>
              </GlassCard>
            </div>

            <div id="finance-section" className="space-y-6">
              <div className="animate-in fade-in duration-300">
                <label className={Label}>Model Comisionare <span className="text-red-500">*</span></label>
                <Select
                  {...commonSelectProps}
                  value={commissionModel}
                  onChange={val => {
                    setCommissionModel(val);
                    setFormData(prev => ({ ...prev, commissionModel: val }));
                  }}
                  options={COMMISSION_MODELS.map(m => ({
                    label: <span className="text-white">{m.value}</span>,
                    value: m.code
                  }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-900/40 p-4 rounded-2xl border border-slate-800 items-center">
                <div className="md:col-span-2">
                  <label className={Label}>
                    {commissionModel === "DEDUCT_FROM_PRICE" ? "Preț Brut Public Listat (EUR)" : "Preț Net Dorit Câștig (EUR)"} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    className={`bg-transparent text-4xl font-black text-white w-full outline-none placeholder:text-slate-700 ${(touched.price || isSubmitted) && (!isNotEmpty(price) || Number(price) <= 0) ? "text-red-500" : ""}`}
                    placeholder="0.000"
                    value={price}
                    onChange={e => setPrice(e.target.value === "" ? "" : parseFloat(e.target.value))}
                  />
                </div>
                <div>
                  <label className={Label}>Cantitate Disponibilă</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-[#334155] text-white font-bold text-sm focus:border-blue-500 outline-none transition-all"
                    value={formData.quantity || ""}
                    onChange={e => setFormData({...formData, quantity: +e.target.value})}
                  />
                </div>
              </div>

              {Number(price) > 0 && (
                <div className="bg-blue-600/5 border border-blue-500/10 rounded-2xl p-5 space-y-3 font-mono text-xs">
                  <div className="flex justify-between text-slate-400">
                    <span>PREȚ AFIȘAT PE MARKETPLACE (BRUT):</span>
                    <span className="font-bold text-white text-sm">{computedValues.grossValue.toLocaleString()} EUR</span>
                  </div>
                  <div className="flex justify-between text-slate-400 border-b border-slate-800/60 pb-2">
                    <span>REȚINERE COMISION PLATFORMĂ ({(computedValues.rate * 100).toFixed(1)}%):</span>
                    <span className="font-bold text-red-400">-{computedValues.commissionValue.toLocaleString()} EUR</span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-500/10 p-3 rounded-xl text-blue-400 border border-blue-500/20">
                    <Info size={16} className="shrink-0" />
                    <div className="flex-1 flex justify-between items-center">
                      <span className="font-bold text-[10px] uppercase tracking-wider">VEI PRIMI ÎN CONT (NET):</span>
                      <span className="text-xl font-black text-emerald-400">{computedValues.netValue.toLocaleString()} EUR</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div id="media-section">
              <GlassCard title="Media Center" icon={UploadCloud}>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <label className={Label}>
                      Media Files <span className="text-red-500">*</span>
                    </label>
                    {!hasMedia && (
                      <span className="text-[10px] text-red-500 font-bold uppercase tracking-tight">
                        {getDictValue("aircraft.part.media.mandatory") || "Fișierele media sunt obligatorii"}
                      </span>
                    )}
                  </div>

                  <label className={`group relative border-2 border-dashed transition-all rounded-3xl p-12 flex flex-col items-center justify-center cursor-pointer overflow-hidden ${
                    isSubmitted && !hasMedia
                      ? "border-red-500/80 bg-red-500/5 shadow-[0_0_15px_rgba(239,68,68,0.1)]"
                      : "border-slate-700 hover:border-emerald-500/50 bg-slate-900/20"
                  }`}>
                    <div className="relative z-10 flex flex-col items-center">
                      <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4">
                        <UploadCloud className="w-8 h-8 text-emerald-500" />
                      </div>
                      <h4 className="text-white font-bold mb-1 uppercase tracking-tighter">Încarcă Imagini Piesă</h4>
                      <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Sarcina Max 50MB</p>
                    </div>
                    <input type="file" multiple className="hidden" onChange={handleMediaChange} />
                  </label>

                  {(existingMedia.length > 0 || mediaFiles.length > 0) && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {existingMedia.map((img) => (
                        <div key={`existing-${img.id}`} className="group relative bg-slate-800 border border-slate-700 rounded-2xl aspect-square overflow-hidden shadow-lg">
                          <img
                            src={img.url}
                            alt="Aircraft part asset preview"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://placehold.co/300?text=Missing+Asset';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setExistingMedia(prev => prev.filter(item => item.id !== img.id));
                            }}
                            className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-red-600 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          >
                            <Trash2 size={12} />
                          </button>
                          <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-slate-900/80 text-[9px] font-mono font-bold tracking-widest text-emerald-400 rounded border border-emerald-500/20 uppercase">
                            Live
                          </span>
                        </div>
                      ))}

                      {mediaFiles.map((file, i) => (
                        <div key={`new-file-${i}`} className="group relative bg-slate-800 border border-emerald-500/30 rounded-2xl aspect-square overflow-hidden shadow-lg">
                          <img
                            src={URL.createObjectURL(file)}
                            alt="Staged file preview"
                            className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
                          />
                          <button
                            type="button"
                            onClick={() => removeNewFile(i)}
                            className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-600 text-white rounded-lg"
                          >
                            <Trash2 size={12} />
                          </button>
                          <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-blue-950/80 text-[9px] font-mono font-bold tracking-widest text-blue-400 rounded border border-blue-500/20 uppercase animate-pulse">
                            New
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </GlassCard>
            </div>
          </div>

          <div className="space-y-8">
            <div id="technical-section">
              <GlassCard title="Informații Tehnice" icon={FileText}>
                <div className="flex flex-col h-full min-h-[400px]">
                  <label className={Label}>Descriere și Condiție Tehnică <span className="text-red-500">*</span></label>
                  <textarea
                    className={`flex-1 w-full p-4 rounded-xl bg-[#1E293B] border text-white text-sm focus:border-blue-500 outline-none resize-none transition-all ${(touched.description || isSubmitted) && !isNotEmpty(formData.description) ? "border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.1)]" : "border-[#334155]"}`}
                    placeholder="Detalii despre ore de funcționare, istoric service, compatibilitate aeronave..."
                    value={formData.description || ""}
                    onBlur={() => setTouched(prev => ({...prev, description: true}))}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                  />
                  <div className="mt-4 p-4 bg-blue-500/5 border border-blue-500/10 rounded-2xl flex items-start gap-3">
                    <Info size={16} className="text-blue-400 mt-0.5" />
                    <p className="text-[10px] text-slate-400 leading-relaxed uppercase font-bold">
                      Asigurați-vă că includeți detalii despre certificările piesei (EASA Form 1, FAA Form 8130-3) în descriere.
                    </p>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>

          <div className="lg:col-span-2 flex flex-col md:flex-row items-center justify-between gap-6 p-8 bg-[#0F172A]/90 border border-slate-800 rounded-3xl mt-4 shadow-2xl backdrop-blur-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-600/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-900/20">
                <ShieldCheck className="text-emerald-400" size={24} />
              </div>
              <div>
                <h4 className="text-white font-black uppercase text-sm italic tracking-widest">Validare Inventar</h4>
                <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">Procesare securizată pentru piese și echipamente aviatice</p>
              </div>
            </div>
            <div className="flex flex-wrap md:flex-nowrap items-center gap-3 w-full md:w-auto">
              <Button
                variant="ghost"
                type="button"
                onClick={() => navigateTo("dashboard")}
                className="flex-1 md:flex-none h-12 px-6 rounded-xl font-bold uppercase text-xs text-slate-400 hover:text-white hover:bg-slate-800/60 transition-all border border-slate-800/80"
              >
                Renunță
              </Button>
              <Button
                type="button"
                disabled={loading}
                onClick={handleDraftClick}
                className="flex-1 md:flex-none h-12 px-6 rounded-xl font-black uppercase tracking-wider text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700/80 shadow-lg active:scale-95 transition-all flex items-center gap-2"
              >
                <FileText size={15} className="text-slate-400" />
                Salvează Draft
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 md:flex-none h-12 px-8 rounded-xl font-black uppercase italic tracking-wider text-xs bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-lg shadow-emerald-900/40 hover:shadow-emerald-600/30 active:scale-95 transition-all"
              >
                {isEditMode ? "Salvează Modificările" : "Adaugă în Marketplace"}
              </Button>
            </div>
          </div>
        </form>
      </div>
      <ConfirmationAlert
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onConfirm={() => executeSubmit(false)}
        dict={alertDict}
      />
      <ConfirmationAlert
        isOpen={isDraftAlertOpen}
        onClose={() => setIsDraftAlertOpen(false)}
        onConfirm={() => executeSubmit(true)}
        dict={draftAlertDict}
      />
    </div>
  );
};

export default AircraftPart;